import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlarmClock, X } from 'lucide-react';
import { supabase } from '../../src/lib/supabase';
import { useAuth } from '../../src/contexts/AuthContext';
import { abrirSolicitacao } from '../../src/lib/abrirSolicitacao';
import { avisoDoPrazo, diaEmBrasilia, situacaoDoPrazo, type TipoAviso } from '../../src/lib/prazoSolicitacaoVisual';
import { useAgora, SeloUrgencia } from './PrazoSolicitacao';

/**
 * Popup de prazo das solicitações (#6355), em qualquer tela e só para o responsável:
 *   - "aperto": urgência/alta com 2h ou menos, ou atrasada — volta a cada 30 min
 *     ("Lembrar em 30 min") até concluir, trocar o prazo ou bloquear;
 *   - 2 dias úteis, 1 dia útil e no dia — uma vez por dia.
 * Substitui o aviso no sininho (migration 20261020170000). Quem dispensou fica em
 * localStorage, com a chave do usuário (vale só neste navegador).
 */

interface Item {
  id: number;
  titulo: string;
  prioridade: string;
  vencimento: string | null;
  solicitacao_status: string | null;
  bloqueada_por_pessoa_id: string | null;
  bloqueada_por_texto: string | null;
}

const RECARREGAR_MS = 5 * 60_000;
const ADIAR_MS = 30 * 60_000;
const ROTULO: Record<TipoAviso, string> = { aperto: 'Últimas horas', hoje: 'Vence hoje', um_dia: 'Vence no próximo dia útil', dois_dias: 'Vence em 2 dias úteis' };

const ler = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const gravar = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* sem storage: o popup só volta mais cedo */ } };

const AvisoPrazoSolicitacoes: React.FC = () => {
  const { user } = useAuth();
  const agora = useAgora();
  const [itens, setItens] = useState<Item[]>([]);
  const [versao, setVersao] = useState(0);

  const carregar = useCallback(async () => {
    if (!user?.id) return;
    const ate = new Date(Date.now() + 6 * 86_400_000).toISOString().slice(0, 10);
    // Atrasada há mais de 30 dias é passivo, não aperto: não entra no popup de 30 em 30 min.
    const desde = new Date(Date.now() - 30 * 86_400_000).toISOString().slice(0, 10);
    const { data } = await supabase.from('tarefas')
      .select('id, titulo, prioridade, vencimento, solicitacao_status, bloqueada_por_pessoa_id, bloqueada_por_texto')
      .eq('categoria', 'solicitacao')
      .eq('responsavel_id', user.id)
      .not('vencimento', 'is', null)
      .lte('vencimento', ate)
      .gte('vencimento', desde)
      .order('vencimento', { ascending: true })
      // Sem status conta como "novo": NOT IN sozinho deixaria essas de fora.
      .or('solicitacao_status.is.null,solicitacao_status.not.in.(concluido,rejeitado)')
      .limit(100);
    setItens((data ?? []) as Item[]);
  }, [user?.id]);

  useEffect(() => {
    carregar();
    const t = setInterval(carregar, RECARREGAR_MS);
    return () => clearInterval(t);
  }, [carregar]);

  const chaveAdiado = `vh_${user?.id}_prazo_aperto_ate`;
  const chaveDia = `vh_${user?.id}_prazo_visto_em`;
  const hoje = diaEmBrasilia(agora);

  const { aperto, doDia } = useMemo(() => {
    const comAviso = itens.map(t => ({ t, tipo: avisoDoPrazo(t, agora) })).filter(x => x.tipo) as { t: Item; tipo: TipoAviso }[];
    return { aperto: comAviso.filter(x => x.tipo === 'aperto'), doDia: comAviso.filter(x => x.tipo !== 'aperto') };
    // `versao` força recalcular depois de dispensar.
  }, [itens, agora, versao]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!user?.id) return null;
  const adiadoAte = Number(ler(chaveAdiado) || 0);
  const mostrarAperto = aperto.length > 0 && agora.getTime() >= adiadoAte;
  const mostrarDia = doDia.length > 0 && ler(chaveDia) !== hoje;
  if (!mostrarAperto && !mostrarDia) return null;

  const listaToda = [...(mostrarAperto ? aperto : []), ...(mostrarDia ? doDia : [])];
  const lista = listaToda.slice(0, 8);
  const fechar = () => {
    if (mostrarAperto) gravar(chaveAdiado, String(Date.now() + ADIAR_MS));
    if (mostrarDia) gravar(chaveDia, hoje);
    setVersao(v => v + 1);
  };

  return (
    // Não toma o foco (apareceria no meio de quem está digitando), então não é alertdialog: role="alert" anuncia
    // o aperto na hora e "status" anuncia sem interromper o aviso do dia. Esc fecha quando o foco está dentro.
    <div
      role={mostrarAperto ? 'alert' : 'status'} aria-labelledby="aviso-prazo-titulo"
      onKeyDown={e => { if (e.key === 'Escape') fechar(); }}
      className="fixed bottom-6 right-6 z-[120] w-[min(24rem,calc(100vw-2rem))] bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
    >
      <div className={`flex items-center gap-2 px-4 py-3 ${mostrarAperto ? 'bg-red-600 text-white' : 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'}`}>
        <AlarmClock size={18} aria-hidden="true" />
        <h2 id="aviso-prazo-titulo" className="text-sm font-bold flex-1">
          {mostrarAperto ? 'Solicitação com prazo apertado' : 'Prazos chegando'}
        </h2>
        <button onClick={fechar} aria-label="Fechar aviso" className="p-1 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-current"><X size={16} /></button>
      </div>
      <ul className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        {lista.map(({ t, tipo }) => (
          <li key={t.id} className="px-4 py-3">
            <div className="flex items-center gap-1.5 mb-1">
              {t.prioridade === 'urgencia' && <SeloUrgencia />}
              <span className={`text-[10px] font-bold uppercase ${tipo === 'aperto' ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                {/* Atrasada já diz "Atrasada há 3h": "Últimas horas · Atrasada há 3h" se contradizia. */}
                {situacaoDoPrazo(t, agora).nivel === 'atrasada' ? situacaoDoPrazo(t, agora).texto : `${ROTULO[tipo]} · ${situacaoDoPrazo(t, agora).texto}`}
              </span>
            </div>
            <button
              onClick={() => { abrirSolicitacao(String(t.id)); fechar(); }}
              className="text-left text-sm font-semibold text-gray-900 dark:text-white underline decoration-dotted underline-offset-2 hover:text-primary dark:hover:text-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm line-clamp-2"
            >
              #{t.id} {t.titulo}
            </button>
          </li>
        ))}
      </ul>
      {listaToda.length > lista.length && (
        <p className="px-4 py-2 text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">e mais {listaToda.length - lista.length} — veja na <a href="#/tarefas/solicitacoes" onClick={fechar} className="font-semibold underline hover:text-primary dark:hover:text-primary-light focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-sm">Central de Solicitações</a>.</p>
      )}
      <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
        {mostrarAperto && lista.length === 1 && (
          <button onClick={() => { abrirSolicitacao(String(lista[0].t.id)); fechar(); }} className="px-3 py-1.5 text-xs font-semibold text-white bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
            Abrir solicitação
          </button>
        )}
        <button onClick={fechar} className="px-3 py-1.5 text-xs font-semibold text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40">
          {mostrarAperto ? 'Lembrar em 30 min' : 'Ok, entendi'}
        </button>
      </div>
    </div>
  );
};

export default AvisoPrazoSolicitacoes;
